import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Wrench, X, Check, Printer, Package, Warning, CircleNotch, ShoppingCart } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import TyroPaymentDialog from "./TyroPaymentDialog";
import SaleCompleteCard from "./SaleCompleteCard";

/**
 * Confirm Booking Modal — mirrors the Ticket Wizard "Finish · Create & print" step.
 *
 * Layout:
 *   HEADER       Booking title + close
 *   LEFT CARD    READY TO CREATE: device heroline, customer, issue line(s), TOTAL + auto-print status
 *   RIGHT CARD   PAYMENT (with "Collect now" toggle): method, tendered, presets, change due
 *   FOOTER       CREATE TICKET primary button
 *
 * Behaviour:
 *   - Warranty defaults to 12 months (shop policy), not user-editable.
 *   - Estimated price defaults to the booking's `estimated_cost` (price chosen on website).
 *     Staff can override before creating the ticket.
 *   - "Collect now" ON → creates ticket + sale + fires auto-print label AND receipt.
 *   - "Collect now" OFF → creates ticket + fires auto-print label + pickup slip.
 */
const FIXED_WARRANTY = 12;

export default function ConfirmBookingModal({ open, booking, onClose, onConverted }) {
  const { currentStore } = useStore();

  // Pricing
  const bookingPrice = Number(booking?.estimated_cost || 0);
  const [estimate, setEstimate] = useState(bookingPrice);

  // Payment
  const [collectPayment, setCollectPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  // Tyro EFTPOS dialog — opened when staff picks the Tyro method and clicks
  // "Pay & Create". On approval we re-enter submit(tyroMeta) which carries
  // the auth code + RRN into the Sale row for refund linking.
  const [tyroOpen, setTyroOpen] = useState(false);
  // Split-tender state — mirrors the wizard. When on, the Method buttons
  // are hidden and 4 amount rows (cash / card / Tyro / other) replace
  // the single Amount-tendered input. On submit, a Tyro slice (if any)
  // opens the dialog first; after approval the sale POSTs with a
  // `payments[]` array carrying every method + Tyro auth_code on the
  // Tyro row, identical to the wizard's batch payload.
  const [splitMode, setSplitMode] = useState(false);
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  // Captures the freshly-created sale + ticket so the unified
  // SaleCompleteCard can morph the Tyro dialog (or pop standalone for
  // cash / card paths). Cleared by the cashier tapping Done, which
  // also performs the final navigation to the new repair page.
  const [pendingSale, setPendingSale] = useState(null); // { sale, ticket, customer }
  const [splitTyro, setSplitTyro] = useState("");
  const [splitOther, setSplitOther] = useState("");
  const [tyroSplitOpen, setTyroSplitOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const convertRef = useRef(null);
  const saleIdemRef = useRef(null);
  const [orderNudge, setOrderNudge] = useState(null);
  const [markingOrder, setMarkingOrder] = useState(false);

  // Print prefs (scoped to active store)
  const [prefs, setPrefs] = useState({});
  const [printConfigured, setPrintConfigured] = useState(false);

  // Parts-Inventory match for this booking — populates the "Parts & stock"
  // panel so staff see availability before converting.
  const [partMatch, setPartMatch] = useState(null);
  const [partMatchLoading, setPartMatchLoading] = useState(false);
  // Grade picker: per-part index → selected option_id (grade-tracked repairs).
  const [gradeSel, setGradeSel] = useState({});

  // Reset on open / booking change
  useEffect(() => {
    if (!open) return;
    setEstimate(Number(booking?.estimated_cost || 0));
    setCollectPayment(false);
    setPaymentMethod("cash");
    setTendered("");
    setSplitMode(false); setSplitCash(""); setSplitCard(""); setSplitTyro(""); setSplitOther("");
    setTyroOpen(false); setTyroSplitOpen(false);
    setOrderNudge(null); setMarkingOrder(false);
    api.get("/print/status").then((r) => setPrintConfigured(!!r.data?.configured)).catch(() => {});
    api.get("/users/me/print-prefs").then((r) => setPrefs(r.data || {})).catch(() => {});
    // Resolve which inventory part(s) this booking maps to + live stock.
    setPartMatch(null); setPartMatchLoading(true); setGradeSel({});
    api.get(`/bookings/${booking.id}/part-match`)
      .then((r) => {
        setPartMatch(r.data);
        // Default-select each grade-tracked repair to the resolved grade.
        const init = {};
        (r.data?.parts || []).forEach((p, i) => {
          if (p.grade_tracked && p.options?.length) {
            const match = p.options.find((o) => o.checklist_key === p.checklist_key) || p.options[0];
            init[i] = match.option_id;
          }
        });
        setGradeSel(init);
      })
      .catch(() => setPartMatch(null))
      .finally(() => setPartMatchLoading(false));
  }, [open, booking?.id, currentStore?.id]);

  const money = (n) => Number(n || 0).toFixed(2);
  const total = useMemo(() => Math.round(Number(estimate || 0) * 100) / 100, [estimate]);
  const tenderedNum = Number(tendered || 0);
  const totalCents = Math.round(total * 100);
  const tenderedCents = Math.round(tenderedNum * 100);
  const shortOnTender = collectPayment && tenderedCents < totalCents;
  const isDeposit = collectPayment && shortOnTender && tenderedCents > 0;
  const change = Math.max(0, (tenderedCents - totalCents) / 100);
  const balanceDue = Math.max(0, (totalCents - tenderedCents) / 100);

  if (!open || !booking) return null;

  const device = [booking.device_make, booking.device_model].filter(Boolean).join(" ") || "— device not specified —";
  const issueLines = (booking.problem || "").split("\n").filter(Boolean);

  const labelAutoOn = printConfigured && prefs?.auto_print_label && prefs?.label_printer_id;
  const receiptAutoOn = printConfigured && prefs?.auto_print_receipt && prefs?.receipt_printer_id;

  // Resolve the currently-selected grade option for a part row (or null).
  const selectedOption = (p, i) => {
    if (!p?.grade_tracked || !p.options?.length) return null;
    return p.options.find((o) => o.option_id === gradeSel[i]) || p.options[0];
  };
  // The checklist key to deduct for a part, honouring the grade picker.
  const effectiveKey = (p, i) => {
    const sel = selectedOption(p, i);
    if (sel) return sel.checklist_key;
    return p?.checklist_key || null;
  };
  // Final keys sent to convert so the chosen grade(s) get deducted.
  const effectivePartKeys = () =>
    (partMatch?.parts || [])
      .map((p, i) => effectiveKey(p, i))
      .filter(Boolean);
  // Does any selected grade have zero stock? Drives the pre-create warning.
  const anySelectedOut = (partMatch?.parts || []).some((p, i) => {
    const sel = selectedOption(p, i);
    if (sel) return sel.tracked && (sel.stock || 0) <= 0;
    return p?.tracked && (p.stock || 0) <= 0;
  });
  const pickGrade = (i, opt) => {
    setGradeSel((prev) => ({ ...prev, [i]: opt.option_id }));
    if (opt.price != null && Number(opt.price) > 0) setEstimate(Number(opt.price));
  };

  const firePrint = async (path) => {
    try {
      await api.post(path.url, { printer_id: path.printer, copies: 1 });
    } catch (e) {
      toast.error(`${path.label} print failed: ${formatApiError(e)}`);
    }
  };

  const submit = async (tyroMeta = null) => {
    const splitCashN = Number(splitCash || 0);
    const splitCardN = Number(splitCard || 0);
    const splitTyroN = Number(splitTyro || 0);
    const splitOtherN = Number(splitOther || 0);
    const splitSum = splitCashN + splitCardN + splitTyroN + splitOtherN;
    const effectiveTendered = splitMode ? splitSum : tenderedNum;
    const effectiveTenderedCents = Math.round(effectiveTendered * 100);
    const isSplit = splitMode && splitSum > 0;

    if (collectPayment && !isSplit && effectiveTenderedCents <= 0 && total > 0 && paymentMethod !== "tyro") {
      return toast.error("Enter a tendered amount (or tick the exact preset)");
    }
    // Tyro charges the FULL total via the terminal (single tender), OR
    // just the Tyro slice in split mode — pop the dialog first. On
    // approval we re-enter submit(meta) which routes through card_manual
    // + tyro_meta sidecar for single tender, or a `payments[]` row with
    // `method: "tyro"` for split tender.
    if (collectPayment && !splitMode && paymentMethod === "tyro" && !tyroMeta) {
      if (total <= 0) return toast.error("Total must be greater than $0 to charge Tyro");
      setTyroOpen(true);
      return;
    }
    if (collectPayment && splitMode && splitTyroN > 0 && !tyroMeta) {
      if (splitTyroN > total + 0.01) {
        return toast.error(`Tyro slice ($${splitTyroN.toFixed(2)}) exceeds total ($${total.toFixed(2)})`);
      }
      setTyroSplitOpen(true);
      return;
    }
    const isDepositSplit = isSplit && splitSum + 0.005 < total;
    if ((isDeposit || isDepositSplit) && !tyroMeta) {
      const shown = isSplit ? splitSum : tenderedNum;
      const bal = Math.max(0, total - shown);
      const ok = window.confirm(
        `Take a deposit of $${shown.toFixed(2)}?\n\nBalance due: $${bal.toFixed(2)}\n\nThe customer can settle the balance later from the invoice page.`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      // 1) Create ticket from booking (uses booking price, or override)
      // Reuse an earlier conversion on Retry save so the ticket isn't
      // created twice after an approved Tyro charge.
      const convert = convertRef.current || (await api.post(`/bookings/${booking.id}/convert`, {
        estimated_cost: Number(estimate || 0),
        warranty_months: FIXED_WARRANTY,
        part_keys: effectivePartKeys(),
      })).data;
      convertRef.current = convert;
      if (!saleIdemRef.current) saleIdemRef.current = `bk-${booking.id}-${Date.now()}`;

      // 2) Optionally create the sale right now
      let sale = null;
      if (collectPayment && total > 0) {
        const isTyroSingle = !splitMode && paymentMethod === "tyro" && !!tyroMeta;
        const salePayload = {
          items: [{
            product_id: null,
            sku: convert.ticket_number,
            name: device,
            quantity: 1,
            unit_price: Number(estimate || 0),
            line_total: Number(estimate || 0),
            kind: "repair",
            repair_ticket_id: convert.ticket_id,
          }],
          discount: 0,
          tax: 0,
          // Headline method: card_manual when single-tender Tyro (server
          // enum constraint); for split we pass the original picker
          // method but it's overridden by the dominant amount in
          // `payments[]` server-side.
          payment_method: isTyroSingle ? "card_manual" : (splitMode ? "cash" : paymentMethod),
          amount_tendered: isTyroSingle ? total : (isSplit ? splitSum : (tenderedNum || total)),
          customer_id: booking.customer_id || null,
          ...(isTyroSingle ? { tyro_meta: tyroMeta } : {}),
          // Split-tender payload — only sent when split mode is on so
          // single-tender flows keep their tighter SaleCreate shape.
          ...(isSplit
            ? {
                payments: [
                  splitCashN > 0 && { amount: splitCashN, method: "cash" },
                  splitCardN > 0 && { amount: splitCardN, method: "card_manual" },
                  splitTyroN > 0 && {
                    amount: splitTyroN,
                    method: "tyro",
                    ...(tyroMeta ? { tyro_meta: tyroMeta } : {}),
                  },
                  splitOtherN > 0 && { amount: splitOtherN, method: "other" },
                ].filter(Boolean),
              }
            : {}),
        };
        const { data: s } = await api.post("/sales", salePayload, { headers: { "Idempotency-Key": saleIdemRef.current } });
        sale = s;
      }

      toast.success(
        collectPayment
          ? `Ticket ${convert.ticket_number} + Sale ${sale?.sale_number || ""}`
          : `Ticket ${convert.ticket_number} created`
      );
      if (convert.any_out_of_stock) {
        toast.warning(`Part out of stock — ${convert.ticket_number} set to Waiting Parts`, { duration: 6000 });
      }

      // 3) Fire auto-prints (respect Settings toggles — silently no-op if off)
      if (labelAutoOn) {
        firePrint({ url: `/print/label/${convert.ticket_id}`, printer: prefs.label_printer_id, label: "Label" });
      }
      if (collectPayment && sale && receiptAutoOn) {
        firePrint({ url: `/print/receipt/${sale.id}`, printer: prefs.receipt_printer_id, label: "Receipt" });
      } else if (!collectPayment && receiptAutoOn) {
        firePrint({ url: `/print/ticket-slip/${convert.ticket_id}`, printer: prefs.receipt_printer_id, label: "Pickup slip" });
      }

      onConverted?.(convert);

      const lowStockNudges = convert.low_stock_nudges || [];

      // Stash the new sale + ticket so the unified SaleCompleteCard
      // can take over the screen (Tyro dialogs morph into it via
      // `completedSale`; cash/card flows get a standalone overlay).
      // The actual close + navigate happens when the cashier taps
      // Done in the action panel — `closePendingAndNavigate` below.
      if (collectPayment && sale) {
        setPendingSale({
          sale,
          ticket: { ticket_id: convert.ticket_id, ticket_number: convert.ticket_number },
          customer: booking?.customer || null,
        });
      }
      if (lowStockNudges.length) {
        setOrderNudge({
          bookingId: booking.id,
          ticketId: convert.ticket_id,
          ticketNumber: convert.ticket_number,
          parts: lowStockNudges,
          hasSale: Boolean(collectPayment && sale),
        });
      } else if (!collectPayment || !sale) {
        // No payment leg → keep the legacy "close + jump to repair" flow.
        onClose?.();
        window.location.assign(`/repairs/${convert.ticket_id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      if (tyroMeta) throw e; // Tyro dialog keeps the approval + Retry save
    } finally {
      setSubmitting(false);
    }
  };

  const closeOrderNudge = () => {
    const nudge = orderNudge;
    setOrderNudge(null);
    if (!nudge?.hasSale && nudge?.ticketId) {
      onClose?.();
      window.location.assign(`/repairs/${nudge.ticketId}`);
    }
  };

  const markPartsToOrder = async () => {
    if (!orderNudge) return;
    setMarkingOrder(true);
    try {
      const { data } = await api.post(`/bookings/${orderNudge.bookingId}/mark-parts-to-order`, {
        part_keys: orderNudge.parts.map((part) => part.checklist_key),
      });
      const count = data?.marked?.length || orderNudge.parts.length;
      toast.success(`${count} part${count === 1 ? "" : "s"} marked to order`);
      closeOrderNudge();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarkingOrder(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="confirm-booking-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-8 pt-6 pb-5 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
              <Wrench size={20} weight="bold" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-400 font-bold">
                CONFIRM BOOKING · {booking.external_ref || booking.id.slice(0, 8).toUpperCase()}
              </div>
              <div className="text-[22px] font-bold text-[#121216] leading-tight mt-0.5">
                Finish <span className="font-normal text-slate-400">· Create & print</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
            data-testid="confirm-booking-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-8 pt-6 pb-4">
          <div className="text-[22px] font-bold text-[#121216] tracking-tight">Ready to go</div>
          <div className="text-[13px] text-slate-500 mt-1">
            Create the ticket now. Optionally collect payment upfront.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-8 pb-4">
          {/* LEFT — ready-to-create summary */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
              READY TO CREATE
            </div>
            <div className="mt-3 text-[22px] font-bold text-[#121216] leading-tight">
              {device}
            </div>
            <div className="text-[13px] text-slate-500 mt-0.5">
              {booking.customer_name}
              {booking.customer_phone && <span className="ml-2 text-slate-400 font-mono text-[12px]">{booking.customer_phone}</span>}
            </div>

            {issueLines.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4 space-y-2">
                {issueLines.map((ln, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[13px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-slate-700 truncate">· {ln}</span>
                      {i === 0 && ((partMatch?.parts?.[0] && selectedOption(partMatch.parts[0], 0)?.label) || booking.part_option) && (
                        <span
                          className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E5FEB] border border-[#BFDBFE]"
                          data-testid="confirm-booking-part-option"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1E5FEB]" />
                          {(partMatch?.parts?.[0] && selectedOption(partMatch.parts[0], 0)?.label) || booking.part_option}
                        </span>
                      )}
                    </div>
                    {i === 0 && (
                      Number(estimate || 0) > 0
                        ? <span className="font-mono font-semibold text-[#121216]">${money(estimate)}</span>
                        : <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">NO PRICE · SET BELOW</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* TOTAL row */}
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-baseline justify-between">
              <span className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-bold">TOTAL</span>
              <div className={`flex items-baseline gap-0.5 rounded-lg px-2 py-1 transition-colors ${Number(estimate) <= 0 ? "bg-amber-50 ring-2 ring-amber-200" : ""}`}>
                <span className="text-[28px] font-bold text-[#121216] font-mono">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                  autoFocus={Number(estimate) <= 0}
                  className="w-28 text-[28px] font-bold text-[#121216] bg-transparent text-right font-mono outline-none focus:bg-white/50 rounded px-1 transition-colors"
                  data-testid="confirm-booking-total"
                />
              </div>
            </div>
            {bookingPrice > 0 && Math.abs(Number(estimate) - bookingPrice) > 0.001 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 mt-2">
                Website booking price was ${money(bookingPrice)}
              </div>
            )}
            {bookingPrice === 0 && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 mt-2 leading-relaxed">
                Website booking had no price set. Enter the agreed estimate above before creating the ticket.
              </div>
            )}

            {/* Parts & stock — detected from Parts Inventory */}
            <div className="mt-4 border-t border-slate-100 pt-4" data-testid="confirm-booking-parts-stock">
              <div className="flex items-center gap-2 mb-2">
                <Package size={13} weight="bold" className="text-slate-500" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold">Parts &amp; stock</span>
                {partMatchLoading && <CircleNotch size={12} weight="bold" className="text-slate-400 animate-spin" />}
              </div>
              {partMatchLoading ? (
                <div className="text-[12px] text-slate-400">Checking inventory…</div>
              ) : (partMatch?.parts?.length ? (
                <div className="space-y-2.5">
                  {partMatch.parts.map((p, i) => {
                    const sel = selectedOption(p, i);
                    // Effective stock/status reflects the picked grade.
                    const tracked = sel ? sel.tracked : p.tracked;
                    const status = sel ? sel.status : p.status;
                    const stock = sel ? sel.stock : p.stock;
                    const chip = !tracked
                      ? { cls: "bg-slate-100 text-slate-500 border-slate-200", label: p.status === "unmatched" ? "Not in catalog" : "Not tracked" }
                      : status === "out"
                        ? { cls: "bg-red-50 text-red-700 border-red-200", label: "Out of stock" }
                        : status === "low"
                          ? { cls: "bg-amber-50 text-amber-700 border-amber-200", label: `Low · ${stock}` }
                          : { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `In stock · ${stock}` };
                    return (
                      <div key={i} data-testid={`parts-stock-row-${i}`} className="rounded-xl border border-slate-200/80 bg-white p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex items-center gap-1.5">
                            <Wrench size={13} weight="fill" className="text-slate-400 shrink-0" />
                            <span className="text-[13px] font-semibold text-slate-800 truncate">{p.repair_name}</span>
                          </div>
                          <span className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.04em] uppercase px-2 py-1 rounded-full border ${chip.cls}`} data-testid={`parts-stock-chip-${i}`}>
                            {tracked && (
                              <span className={`w-1.5 h-1.5 rounded-full ${status === "out" ? "bg-red-500" : status === "low" ? "bg-amber-500" : "bg-emerald-500"}`} />
                            )}
                            {chip.label}
                          </span>
                        </div>

                        {/* Grade picker — professional selectable cards */}
                        {p.grade_tracked && p.options?.length > 1 && (
                          <div className="mt-2.5" data-testid={`grade-picker-${i}`}>
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-1.5 px-0.5">Select grade</div>
                            <div className="grid gap-1.5">
                              {p.options.map((o) => {
                                const active = o.option_id === gradeSel[i];
                                const isOut = o.tracked && (o.stock || 0) <= 0;
                                const dot = !o.tracked ? "bg-slate-300"
                                  : o.status === "out" ? "bg-red-500"
                                  : o.status === "low" ? "bg-amber-500" : "bg-emerald-500";
                                const badge = !o.tracked
                                  ? { cls: "text-slate-400", txt: "Not tracked" }
                                  : o.status === "out" ? { cls: "text-red-600", txt: "Out of stock" }
                                  : o.status === "low" ? { cls: "text-amber-600", txt: `${o.stock} left` }
                                  : { cls: "text-emerald-600", txt: `${o.stock} in stock` };
                                return (
                                  <button
                                    key={o.option_id}
                                    type="button"
                                    onClick={() => pickGrade(i, o)}
                                    data-testid={`grade-opt-${i}-${o.option_id}`}
                                    className={`group w-full flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all duration-150 ${
                                      active
                                        ? "border-[#1E5FEB] bg-[#1E5FEB]/[0.045] ring-1 ring-[#1E5FEB]/25 shadow-sm"
                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/70"
                                    }`}
                                    title={o.tracked ? `${o.stock} in stock` : "Not tracked in inventory"}
                                  >
                                    {/* radio */}
                                    <span className={`shrink-0 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-colors ${active ? "border-[#1E5FEB]" : "border-slate-300 group-hover:border-slate-400"}`}>
                                      {active && <span className="w-2 h-2 rounded-full bg-[#1E5FEB]" />}
                                    </span>
                                    {/* label + price */}
                                    <div className="min-w-0 flex-1">
                                      <div className={`text-[12.5px] font-semibold truncate ${active ? "text-[#1E3A8A]" : "text-slate-700"}`}>{o.label}</div>
                                      {o.price != null && Number(o.price) > 0 && (
                                        <div className="text-[11px] text-slate-400 font-medium">${money(o.price)}</div>
                                      )}
                                    </div>
                                    {/* stock */}
                                    <span className={`shrink-0 inline-flex items-center gap-1.5 text-[10.5px] font-bold ${badge.cls}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                      {badge.txt}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[12px] text-slate-400">No matching inventory part found for this booking.</div>
              ))}

              {/* Out-of-stock warning — reflects the picked grade */}
              {!partMatchLoading && anySelectedOut && (
                <div className="mt-2.5 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5" data-testid="parts-out-of-stock-warning">
                  <Warning size={15} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-red-700 leading-snug">
                    The selected grade is <span className="font-bold">out of stock</span>. The ticket will be created and set to <span className="font-bold">Waiting Parts</span>. Stock is deducted when the part is used.
                  </div>
                </div>
              )}
            </div>

            {/* Auto-print label indicator */}
            <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${labelAutoOn ? "bg-[#1E5FEB] text-white" : "bg-slate-200 text-slate-400"}`}>
                <Printer size={14} weight="bold" />
              </div>
              <div className="flex-1">
                <div className="text-[12px] font-semibold text-[#121216]">Auto-print label</div>
                <div className="text-[10px] text-slate-400">
                  {labelAutoOn ? "Will print on create" : "Configure in Settings → Printing"}
                </div>
              </div>
              <div className={`w-10 h-6 rounded-full relative transition-colors ${labelAutoOn ? "bg-[#1E5FEB]" : "bg-slate-200"}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${labelAutoOn ? "left-[18px]" : "left-0.5"}`} />
              </div>
            </div>
          </div>

          {/* RIGHT — payment */}
          <div className={`rounded-2xl border p-5 transition-colors ${collectPayment ? "border-[#1E5FEB] bg-blue-50/20" : "border-slate-200 bg-slate-50/40"}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                PAYMENT
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="confirm-booking-collect-now-label">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${collectPayment ? "bg-[#1E5FEB] border-[#1E5FEB]" : "border-slate-300 bg-white"}`}>
                  {collectPayment && <Check size={12} weight="bold" className="text-white" />}
                </div>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={collectPayment}
                  onChange={(e) => setCollectPayment(e.target.checked)}
                  data-testid="confirm-booking-collect-now"
                />
                <span className="text-[13px] text-slate-700 font-semibold">Collect now</span>
              </label>
            </div>

            <div className={`mt-5 transition-opacity ${collectPayment ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                  {splitMode ? "SPLIT TENDER" : "METHOD"}
                </div>
                {/* Split toggle — mirrors the wizard's pill in the
                    Finish-step payment header. When on, the method
                    buttons + amount-tendered block are replaced with 4
                    amount rows (cash / card / Tyro / other) and an
                    auto-balance button. */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !splitMode;
                    setSplitMode(next);
                    if (!collectPayment) setCollectPayment(true);
                    if (next) {
                      const cur = Number(tendered || 0);
                      setSplitCash(cur > 0 ? cur.toFixed(2) : "");
                      setSplitCard(""); setSplitTyro(""); setSplitOther("");
                    } else {
                      setSplitCash(""); setSplitCard(""); setSplitTyro(""); setSplitOther("");
                    }
                  }}
                  data-testid="confirm-booking-split-toggle"
                  className={`text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border transition-colors ${
                    splitMode
                      ? "border-[#1E5FEB] bg-[#1E5FEB] text-white"
                      : "border-slate-300 text-slate-600 hover:border-[#1E5FEB] hover:text-[#1E5FEB]"
                  }`}
                  title="Pay with multiple methods on one ticket"
                >
                  {splitMode ? "Split on" : "Split"}
                </button>
              </div>
              {!splitMode && (() => {
                // Mirror the wizard: gate by `tyro_enabled` only. A missing
                // integration_key surfaces inside the TyroPaymentDialog at
                // click time, not by hiding the button — so merchants who
                // toggled Tyro ON always see the button on this surface.
                const tyroOn = !!currentStore?.tyro_enabled;
                const methods = tyroOn
                  ? [
                      { v: "cash", label: "CASH" },
                      { v: "card", label: "CARD" },
                      { v: "tyro", label: "TYRO" },
                      { v: "other", label: "OTHER" },
                    ]
                  : [
                      { v: "cash", label: "CASH" },
                      { v: "card", label: "CARD" },
                      { v: "other", label: "OTHER" },
                    ];
                return (
                  <div className={`grid gap-2 ${tyroOn ? "grid-cols-4" : "grid-cols-3"}`}>
                    {methods.map((m) => {
                      if (m.v === "tyro") {
                        const active = paymentMethod === "tyro";
                        return (
                          <button
                            key="tyro"
                            type="button"
                            onClick={() => setPaymentMethod("tyro")}
                            data-testid="confirm-booking-method-tyro"
                            title="Charge to a paired Tyro EFTPOS terminal"
                            className={`h-11 rounded-xl text-[11px] font-bold tracking-[0.15em] transition-all flex items-center justify-center gap-1.5 group ${
                              active
                                ? "bg-[#1E5FEB] text-white shadow-md"
                                : "bg-white border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white"
                            }`}
                          >
                            <img
                              src="/tyro-logo.gif"
                              alt="Tyro"
                              className={`h-4 w-auto transition-all ${active ? "brightness-0 invert" : "group-hover:brightness-0 group-hover:invert"}`}
                            />
                            <span>EFTPOS</span>
                          </button>
                        );
                      }
                      return (
                        <button
                          key={m.v}
                          type="button"
                          onClick={() => setPaymentMethod(m.v)}
                          data-testid={`confirm-booking-method-${m.v}`}
                          className={`h-11 rounded-xl text-[11px] font-bold tracking-[0.15em] transition-all ${
                            paymentMethod === m.v
                              ? "bg-[#121216] text-white shadow-md"
                              : "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"
                          }`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {!splitMode ? (paymentMethod === "tyro" ? (
                <div
                  className="mt-5 rounded-2xl border border-[#1E5FEB]/20 bg-white px-5 py-5 flex items-center justify-between gap-4"
                  data-testid="confirm-booking-tyro-card"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[#1E5FEB] text-[10px] uppercase tracking-[0.18em] font-bold mb-1">
                      <img src="/tyro-logo.gif" alt="" className="h-3 w-auto" />
                      <span>EFTPOS · charging</span>
                    </div>
                    <div className="text-[12px] text-slate-500 leading-snug">
                      Sent to the paired terminal on tap.
                    </div>
                  </div>
                  {(() => {
                    const t = Number(total || 0);
                    const dollars = Math.floor(t);
                    const cents = Math.round((t - dollars) * 100).toString().padStart(2, "0");
                    return (
                      <div className="font-display font-bold tabular-nums leading-none text-[#121216] flex items-start shrink-0">
                        <span className="text-slate-400 text-[18px] font-semibold mt-1 mr-0.5">$</span>
                        <span className="text-[36px] tracking-tight">{dollars.toLocaleString()}</span>
                        <span className="text-slate-400 text-[18px] font-semibold mt-1 ml-0.5">.{cents}</span>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                    AMOUNT TENDERED
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tendered}
                    onChange={(e) => setTendered(e.target.value)}
                    placeholder="0.00"
                    className="w-full h-14 px-4 rounded-xl border border-slate-200 bg-white text-right font-mono text-[22px] font-bold outline-none focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 transition-all"
                    data-testid="confirm-booking-tendered"
                  />

                  <div className="mt-2.5 grid grid-cols-5 gap-1.5">
                    {[
                      { v: total, label: "EXACT" },
                      { v: 20, label: "$20" },
                      { v: 50, label: "$50" },
                      { v: 100, label: "$100" },
                      { v: 200, label: "$200" },
                    ].map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => setTendered(p.v.toString())}
                        data-testid={`confirm-booking-tender-${p.label.replace("$","")}`}
                        className="h-9 rounded-lg bg-white border border-slate-200 hover:border-[#1E5FEB] hover:bg-blue-50 text-[10px] font-bold tracking-wide text-slate-600 transition-all"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {isDeposit ? (
                    <div
                      className="mt-4 flex items-center justify-between px-3 py-2.5 rounded-xl border-2"
                      style={{
                        background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)",
                        borderColor: "#DC2626",
                        boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.08)",
                      }}
                      data-testid="confirm-booking-balance-due"
                    >
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#DC2626] font-bold">BALANCE DUE</span>
                        <div className="text-[10px] font-mono text-[#7F1D1D] mt-0.5">Deposit · ${money(tenderedNum)}</div>
                      </div>
                      <span className="font-mono text-[20px] font-bold text-[#DC2626] tabular-nums">${money(balanceDue)}</span>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-baseline justify-between pt-3 border-t border-slate-200">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">CHANGE DUE</span>
                      <span className="font-mono text-[18px] font-bold text-[#121216]">${money(change)}</span>
                    </div>
                  )}
                </>
              )) : (
                /* ─── SPLIT TENDER ROWS ──────────────────────────────
                   Mirrors the wizard's 4-row split UI. Tyro row is only
                   shown when the store has Tyro enabled. On submit, if
                   splitTyro > 0 the dialog opens for the slice; on
                   approval the sale POSTs with a `payments[]` array
                   carrying every method (and tyro_meta on the Tyro row). */
                <div className="mt-5 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Per-method amounts</div>
                    <button
                      type="button"
                      onClick={() => {
                        const sum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0);
                        const remaining = Math.max(0, total - sum);
                        if (remaining <= 0) return;
                        if (!collectPayment) setCollectPayment(true);
                        const tyroOn = !!currentStore?.tyro_enabled;
                        if (!splitCard && !splitTyro && !splitOther) {
                          if (tyroOn) setSplitTyro(remaining.toFixed(2));
                          else setSplitCard(remaining.toFixed(2));
                        } else if (!splitTyro && tyroOn) setSplitTyro(remaining.toFixed(2));
                        else if (!splitCard) setSplitCard(remaining.toFixed(2));
                        else if (!splitOther) setSplitOther(remaining.toFixed(2));
                        else setSplitCash((Number(splitCash || 0) + remaining).toFixed(2));
                      }}
                      data-testid="confirm-booking-split-balance"
                      className="text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white transition-colors"
                    >
                      Auto-balance
                    </button>
                  </div>
                  {[
                    { key: "cash", label: "CASH", val: splitCash, set: setSplitCash, show: true },
                    { key: "card_manual", label: "CARD", val: splitCard, set: setSplitCard, show: true },
                    { key: "tyro", label: "TYRO", val: splitTyro, set: setSplitTyro, show: !!currentStore?.tyro_enabled },
                    { key: "other", label: "OTHER", val: splitOther, set: setSplitOther, show: true },
                  ].filter((r) => r.show).map((row) => (
                    <div key={row.key} className="flex items-center gap-2">
                      <div className="w-[68px] text-[11px] tracking-wider font-semibold text-slate-600 uppercase shrink-0">{row.label}</div>
                      <div className="flex-1 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-[13px]">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={row.val}
                          onChange={(e) => { row.set(e.target.value); if (!collectPayment && e.target.value) setCollectPayment(true); }}
                          placeholder="0.00"
                          data-testid={`confirm-booking-split-${row.key}`}
                          className="w-full h-11 pl-7 pr-3 font-mono text-right rounded-xl border border-slate-200 bg-white focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none tabular-nums"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 pt-1">
                    <span>Sum tendered</span>
                    <span data-testid="confirm-booking-split-sum" className="tabular-nums font-semibold text-slate-900">
                      ${money(Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0))}
                      {Math.round((Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0)) * 100) === Math.round(total * 100) && total > 0 && (
                        <span className="ml-1.5 text-emerald-600">✓</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-bold tracking-wider text-slate-500 hover:text-slate-800 transition-colors"
            data-testid="confirm-booking-cancel"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={() => submit()}
            disabled={(() => {
              if (submitting || total < 0) return true;
              if (!collectPayment) return false;
              if (total <= 0) return false;
              if (!splitMode && paymentMethod === "tyro") return false; // Tyro charges full total via SDK
              if (splitMode) {
                const sum = Number(splitCash || 0) + Number(splitCard || 0) + Number(splitTyro || 0) + Number(splitOther || 0);
                return sum <= 0;
              }
              return tenderedCents <= 0;
            })()}
            data-testid="confirm-booking-create"
            className={`inline-flex items-center gap-2 h-12 px-7 rounded-xl text-white text-[12px] font-bold tracking-[0.15em] disabled:opacity-40 shadow-lg transition-all ${
              !splitMode && isDeposit && paymentMethod !== "tyro"
                ? "bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#991B1B] shadow-red-500/25"
                : "bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] shadow-blue-500/25"
            }`}
          >
            {submitting
              ? (collectPayment ? "PROCESSING…" : "CREATING…")
              : collectPayment && paymentMethod === "tyro"
                ? "CHARGE TYRO & CREATE"
                : isDeposit
                  ? "TAKE DEPOSIT & CREATE"
                  : (collectPayment ? "PAY & CREATE" : "CREATE TICKET")
            }
            <Check size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
    {tyroOpen && (
      <TyroPaymentDialog
        mode="purchase"
        amountCents={Math.round(total * 100)}
        onClose={() => {
          setTyroOpen(false);
          // If the sale is already saved (we morphed to SAVED and the
          // cashier tapped Done from inside the dialog), perform the
          // deferred navigation now.
          if (pendingSale) {
            const tid = pendingSale.ticket?.ticket_id;
            setPendingSale(null);
            onClose?.();
            if (tid) window.location.assign(`/repairs/${tid}`);
          }
        }}
        onApproved={async (meta) => {
          // Keep the dialog open during submit() so the green Approved
          // screen (checkmark + confetti + "Saving sale…") stays
          // visible — and once `pendingSale` is populated by submit()
          // the dialog smoothly morphs into the SaleCompleteCard.
          await submit(meta);
        }}
        completedSale={pendingSale?.sale || null}
        customer={pendingSale?.customer || null}
        repairTicket={pendingSale?.ticket || null}
        storeForActions={currentStore}
      />
    )}
    {/* Tyro EFTPOS — split-tender slice. Charges ONLY the Tyro amount
        typed into the split row, never the full booking total. On
        approval we re-enter submit(meta) which records the Tyro slice
        with auth_code + RRN on the resulting Sale row, then the
        dialog morphs into the unified SaleCompleteCard. */}
    {tyroSplitOpen && (
      <TyroPaymentDialog
        mode="purchase"
        amountCents={Math.round(Number(splitTyro || 0) * 100)}
        onClose={() => {
          setTyroSplitOpen(false);
          if (pendingSale) {
            const tid = pendingSale.ticket?.ticket_id;
            setPendingSale(null);
            onClose?.();
            if (tid) window.location.assign(`/repairs/${tid}`);
          }
        }}
        onApproved={async (meta) => {
          await submit(meta);
        }}
        completedSale={pendingSale?.sale || null}
        customer={pendingSale?.customer || null}
        repairTicket={pendingSale?.ticket || null}
        storeForActions={currentStore}
      />
    )}
    {orderNudge && (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4" data-testid="low-stock-order-nudge-modal">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="low-stock-order-nudge-title">
          <div className="border-b border-amber-100 bg-amber-50 px-6 py-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm" data-testid="low-stock-order-nudge-icon">
                <ShoppingCart size={19} weight="fill" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">Stock threshold reached</div>
                <h2 id="low-stock-order-nudge-title" className="mt-1 text-lg font-bold text-slate-900">Add this part to ordering?</h2>
              </div>
            </div>
          </div>
          <div className="px-6 py-5">
            <p className="text-sm leading-relaxed text-slate-600" data-testid="low-stock-order-nudge-message">
              This booking brought the selected grade down to its low-stock threshold.
            </p>
            <div className="mt-4 space-y-2" data-testid="low-stock-order-nudge-parts">
              {orderNudge.parts.map((part) => (
                <div key={part.checklist_key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={`low-stock-order-nudge-part-${part.checklist_key.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-800">{part.part_name || "Selected part"}</div>
                    <div className="mt-0.5 text-[11px] font-medium text-slate-500">{part.stock_before} → <span className="font-bold text-amber-700">{part.stock_after} left</span> · threshold {part.threshold}</div>
                  </div>
                  <span className="shrink-0 rounded-full border border-amber-200 bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">Low</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
            <button type="button" onClick={closeOrderNudge} disabled={markingOrder} data-testid="low-stock-order-nudge-skip" className="h-10 px-3 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-800 disabled:opacity-50">Not now</button>
            <button type="button" onClick={markPartsToOrder} disabled={markingOrder} data-testid="low-stock-order-nudge-mark-button" className="inline-flex h-10 items-center gap-2 rounded-lg bg-amber-500 px-4 text-sm font-bold text-white shadow-sm transition-colors hover:bg-amber-600 disabled:opacity-50">
              <ShoppingCart size={15} weight="bold" />{markingOrder ? "Marking…" : "Mark part to order"}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Standalone SaleCompleteCard — used for non-Tyro paths (cash /
        card / other). For Tyro paths the same body renders INSIDE the
        TyroPaymentDialog above (mode="inline"), so we suppress this
        when a Tyro dialog is up. */}
    <SaleCompleteCard
      open={!!pendingSale && !tyroOpen && !tyroSplitOpen}
      onClose={() => {
        const tid = pendingSale?.ticket?.ticket_id;
        setPendingSale(null);
        onClose?.();
        if (tid) window.location.assign(`/repairs/${tid}`);
      }}
      sale={pendingSale?.sale}
      customer={pendingSale?.customer}
      store={currentStore}
      repairTicket={pendingSale?.ticket}
    />
    </>
  );
}
